/**
 * Admin nav section/tab metadata — extracted from AdminDashboard.js to
 * keep the dashboard component focused on rendering.
 *
 * Each section has two tab buckets:
 *   tabs:         shown by default (essential daily workflow)
 *   advancedTabs: hidden until "Advanced ▾" is toggled on the section nav
 *
 * The "Advanced" toggle lives on the section nav row and is per-device
 * (localStorage 'admin-advanced-tabs') so power users can keep it expanded.
 */
import {
    Users, Settings, Shield, Gift, MessageCircle, DollarSign, FileText, Activity,
    Tag, Percent, Link2, Trophy, BarChart3, Flame, Sparkles, Ticket, Megaphone, Search, Banknote, ShieldAlert, UserCog, LifeBuoy, Dice5, Bot, Landmark, Layers, Zap, Gamepad2, Power, Radio, Coins, Fish,
} from 'lucide-react';

export const ADMIN_SECTIONS = [
    {
        id: 'players',
        label: 'Players',
        icon: Users,
        tabs: [
            { value: 'users',         label: 'Users',           icon: Users },
            { value: 'paypal-loads',  label: 'PayPal Loads',    icon: Banknote },
            { value: 'cashouts',      label: 'Cashouts',        icon: DollarSign },
            { value: 'hierarchy',     label: 'Create Customer', icon: Users },
        ],
        advancedTabs: [
            { value: 'transactions', label: 'Trans', icon: Activity },
        ],
    },
    {
        id: 'promotions',
        label: 'Promotions',
        icon: Gift,
        tabs: [
            { value: 'daily-special', label: 'Daily Special', icon: Flame },
            { value: 'series-pack',   label: 'Series Pack',   icon: Trophy },
            { value: 'lucky-pack',    label: 'Lucky 5',       icon: Ticket },
            { value: 'bonus-feed',    label: 'Bonus Feed',    icon: Gift },
            { value: 'ambassadors',   label: 'Ambassadors',   icon: Megaphone },
            { value: 'wheels',        label: 'Wheels',        icon: Sparkles },
            { value: 'promos',        label: 'Promos',        icon: Tag },
            { value: 'freeplay',      label: 'Free Play',     icon: Gift },
            { value: 'announcements', label: 'Announcements', icon: Megaphone },
        ],
        advancedTabs: [
            { value: 'cashback',   label: 'Cashback',   icon: Percent },
            { value: 'referrals',  label: 'Referrals',  icon: Link2 },
            { value: 'promo-perf', label: 'Promo Perf', icon: Gift },
        ],
    },
    {
        id: 'lottery',
        label: 'Lottery',
        icon: Ticket,
        tabs: [
            { value: 'lottery',         label: 'Draws & Settings', icon: Trophy },
        ],
        // Place-a-ticket and flagged review are tucked behind Advanced so the
        // main Lottery view stays focused on draws & settings.
        advancedTabs: [
            { value: 'place-ticket',    label: 'Place Ticket',     icon: Ticket },
            { value: 'flagged-tickets', label: 'Flagged',          icon: ShieldAlert },
        ],
    },
    {
        id: 'sports',
        label: 'Sports',
        icon: Dice5,
        tabs: [
            { value: 'sportsbook', label: 'Sportsbook', icon: Trophy },
        ],
        advancedTabs: [],
    },
    {
        id: 'reports',
        label: 'Reports',
        icon: Activity,
        tabs: [
            { value: 'floor-live', label: 'Floor Live', icon: Radio },
            { value: 'game-players', label: 'Game Players', icon: Users },
            { value: 'settlement', label: 'Weekly Settle', icon: DollarSign },
            { value: 'payback', label: 'Payback', icon: Percent },
            { value: 'daily-profit', label: 'Daily Profit', icon: DollarSign },
            { value: 'games-performance', label: 'Games', icon: Activity },
            { value: 'reports',    label: 'Reports',    icon: Activity },
            { value: 'player-pnl', label: 'Player P&L', icon: Search },
            { value: 'bonus-analytics', label: 'Bonus ROI', icon: Gift },
        ],
        advancedTabs: [
            { value: 'audit',            label: 'Audit',     icon: FileText },
            { value: 'analytics-charts', label: 'Analytics', icon: BarChart3 },
        ],
    },
    {
        id: 'games',
        label: 'Games',
        icon: Gamepad2,
        tabs: [
            { value: 'game-availability', label: 'Availability', icon: Power },
            { value: 'game-rtp',          label: 'RTP & Edge',   icon: Percent },
        ],
        advancedTabs: [],
    },
    {
        id: 'slots',
        label: 'Slots',
        icon: Sparkles,
        tabs: [
            { value: 'buffalo-bucks',  label: 'Buffalo Bucks',  icon: Coins },
            { value: 'bof',            label: 'Balls of Fire',  icon: Flame },
            { value: 'thunder-cash',   label: 'Thunder Cash',   icon: Zap },
            { value: 'panda-paradise', label: 'Panda Paradise', icon: Sparkles },
            { value: 'outlaws-loot',   label: "Outlaw's Loot",  icon: Coins },
            { value: 'deep-sea-furtune', label: 'Deep Sea Furtune', icon: Fish },
            { value: 'jackpots',       label: 'Jackpots',       icon: Trophy },
        ],
        // Lucky Ducky lives inside Slots (accessible via Advanced) rather than
        // as its own top-level section.
        advancedTabs: [
            { value: 'casino-sync',          label: 'Slots Sync',        icon: Activity },
            { value: 'lucky-ducky-profiles', label: 'Lucky Ducky · Math', icon: Shield },
            { value: 'lucky-ducky-reports',  label: 'Lucky Ducky · Reports', icon: FileText },
            { value: 'lucky-ducky-bots',     label: 'Lucky Ducky · Bots',    icon: Bot },
            { value: 'lucky-ducky-qa',       label: 'Lucky Ducky · QA',      icon: Zap },
        ],
    },
    {
        id: 'keno',
        label: 'Keno',
        icon: Sparkles,
        tabs: [
            { value: 'keno-configs', label: 'Games',    icon: Settings },
            { value: 'keno-reports', label: 'Reports',  icon: FileText },
            { value: 'keno-round-lookup', label: 'Round Lookup', icon: Search },
        ],
        advancedTabs: [],
    },
    {
        id: 'settings',
        label: 'Settings',
        icon: Settings,
        tabs: [
            { value: 'settings', label: 'Settings', icon: Settings },
            { value: 'kiosks',   label: 'Kiosks',   icon: Banknote },
            { value: 'support',  label: 'Support',  icon: MessageCircle },
            { value: 'staff',    label: 'Locations', icon: Shield },
            { value: 'cashiers', label: 'Cashiers', icon: UserCog },
            { value: 'help',     label: 'Help Center', icon: LifeBuoy },
            { value: 'role-access', label: 'Role Access', icon: Shield },
        ],
        advancedTabs: [],
    },
];

// Suppress unused-import lint for icons kept for future sections (Dice5/Layers/Landmark).
export const _RESERVED_ICONS = { Dice5, Layers, Landmark };
